import {
  PracticeClient,
  PracticeCase,
  PracticeConsultation,
  PracticeReport,
  TimingRadarEvent,
  FollowUpItem,
  ClientTimelineEvent,
  PracticeProfileSettings,
  PracticeAnalyticsSummary,
  CaseStage,
  ReportStatus,
  ConsultationSessionStatus,
  FollowUpStatus,
  ReportType
} from '../types/practiceManagement';
import { CompleteKundali } from '../types/kundali';
import { StoredKundaliRecord } from './kundaliService';

const DB_STORAGE_KEY = 'v515_practice_management_store_v1';
const SETTINGS_STORAGE_KEY = 'v515_practice_profile_settings_v1';

export const DEFAULT_PRACTICE_SETTINGS: PracticeProfileSettings = {
  practiceName: '',
  tagline: '',
  primaryPractitionerName: '',
  practitionerTitle: '',
  traditionLineage: 'Brihat Parashara Hora Shastra Tradition',
  email: '',
  phone: '',
  location: '',
  website: '',
  consultationFeeDefault: '₹ 5,100',
  defaultCurrency: 'INR',
  officialDisclaimerText:
    'Vedic Astrological consultations provide classical diagnostic assessments of planetary archetypes, karmic tendencies, and auspicious timing horizons. All counsel is intended for dharmic alignment, conscious decision-making, and self-understanding.',
  signatureText: '',
  defaultReportTemplate: 'EXECUTIVE_BRIEF',
  enableAutoTimingRadar: true,
  defaultHorizonDays: 60
};

interface PracticeDatabase {
  clients: PracticeClient[];
  cases: PracticeCase[];
  consultations: PracticeConsultation[];
  reports: PracticeReport[];
  timingEvents: TimingRadarEvent[];
  followUps: FollowUpItem[];
  timeline: ClientTimelineEvent[];
}

// Initial Curated Practice Seed Data (Refined Vedic practice records)
const INITIAL_PRACTICE_DATA: PracticeDatabase = {
  clients: [
    {
      id: 'cli_aarav_01',
      referenceId: 'VJ-CLI-8491',
      name: 'Aarav Sharma',
      gender: 'male',
      dateOfBirth: '1992-06-14',
      timeOfBirth: '07:35',
      placeOfBirth: 'New Delhi, India',
      latitude: 28.6139,
      longitude: 77.209,
      timezoneOffsetMinutes: 330,
      lagnaSign: 'Gemini (Mithuna)',
      moonSign: 'Scorpio (Vrishchika)',
      nakshatra: 'Jyeshtha',
      activeDasha: 'Jupiter-Saturn (Guru-Shani)',
      lifeStage: 'Early Career & Householder (Gruhastha)',
      ageYears: 34,
      phone: '+91 98112 34567',
      email: 'aarav.sharma@example.com',
      primaryConcern: 'Leadership role transition & overseas venture',
      tags: ['Corporate', 'Career', 'Overseas'],
      status: 'ACTIVE',
      openCasesCount: 1,
      lastConsultationDate: '2026-07-15',
      nextFollowUpDate: '2026-09-10',
      hasUpcomingTimingAlert: true,
      upcomingTimingSummary: 'Double-Transit activation on 10th House (Pisces) in 28 days',
      notes: 'Native is senior director in tech evaluating partnership in Singapore.',
      createdAt: '2026-05-10T10:00:00Z',
      updatedAt: '2026-08-20T14:30:00Z'
    },
    {
      id: 'cli_priyanka_02',
      referenceId: 'VJ-CLI-8492',
      name: 'Priyanka Patel',
      gender: 'female',
      dateOfBirth: '1995-11-23',
      timeOfBirth: '14:20',
      placeOfBirth: 'Ahmedabad, Gujarat',
      latitude: 23.0225,
      longitude: 72.5714,
      timezoneOffsetMinutes: 330,
      lagnaSign: 'Aquarius (Kumbha)',
      moonSign: 'Sagittarius (Dhanu)',
      nakshatra: 'Mula',
      activeDasha: 'Venus-Mercury (Shukra-Budha)',
      lifeStage: 'Gruhastha Entry (Marriage Phase)',
      ageYears: 30,
      phone: '+91 98250 88991',
      email: 'priyanka.p@example.com',
      primaryConcern: 'Marriage alliance timing & compatibility verification',
      tags: ['Marriage', 'Matchmaking', 'D9 Navamsha'],
      status: 'FOLLOW_UP_DUE',
      openCasesCount: 1,
      lastConsultationDate: '2026-08-01',
      nextFollowUpDate: '2026-08-30',
      hasUpcomingTimingAlert: true,
      upcomingTimingSummary: '7th Lord transit confluence activating autumn 2026',
      notes: 'Detailed Navamsha and 7th bhava analysis requested by family.',
      createdAt: '2026-06-18T11:20:00Z',
      updatedAt: '2026-08-22T09:15:00Z'
    },
    {
      id: 'cli_vikram_03',
      referenceId: 'VJ-CLI-8493',
      name: 'Vikramaditya Roy',
      gender: 'male',
      dateOfBirth: '1984-03-08',
      timeOfBirth: '22:15',
      placeOfBirth: 'Kolkata, West Bengal',
      latitude: 22.5726,
      longitude: 88.3639,
      timezoneOffsetMinutes: 330,
      lagnaSign: 'Libra (Tula)',
      moonSign: 'Taurus (Vrishabha)',
      nakshatra: 'Rohini',
      activeDasha: 'Saturn-Jupiter (Shani-Guru)',
      lifeStage: 'Mature Career & Wealth Consolidation',
      ageYears: 42,
      phone: '+91 98301 77223',
      email: 'v.roy.ventures@example.com',
      primaryConcern: 'Real estate investment portfolio & legacy business restructuring',
      tags: ['Wealth', 'Real Estate', 'D4 Chaturthamsha'],
      status: 'ACTIVE',
      openCasesCount: 1,
      lastConsultationDate: '2026-08-12',
      nextFollowUpDate: '2026-09-25',
      hasUpcomingTimingAlert: false,
      notes: 'Industrialist seeking timing window for industrial plot purchase in Bengal.',
      createdAt: '2026-07-02T16:45:00Z',
      updatedAt: '2026-08-24T18:00:00Z'
    },
    {
      id: 'cli_ananya_04',
      referenceId: 'VJ-CLI-8494',
      name: 'Ananya Deshmukh',
      gender: 'female',
      dateOfBirth: '2001-09-05',
      timeOfBirth: '05:50',
      placeOfBirth: 'Pune, Maharashtra',
      latitude: 18.5204,
      longitude: 73.8567,
      timezoneOffsetMinutes: 330,
      lagnaSign: 'Leo (Simha)',
      moonSign: 'Pisces (Meena)',
      nakshatra: 'Uttara Bhadrapada',
      activeDasha: 'Mars-Rahu (Mangal-Rahu)',
      lifeStage: 'Brahmacharya / Higher Education',
      ageYears: 24,
      phone: '+91 97654 33211',
      email: 'ananya.desh@example.com',
      primaryConcern: 'Doctoral research admissions in Europe & funding timeline',
      tags: ['Higher Education', 'Abroad', 'D24 Siddhamsa'],
      status: 'ACTIVE',
      openCasesCount: 1,
      lastConsultationDate: '2026-08-18',
      hasUpcomingTimingAlert: true,
      upcomingTimingSummary: '9th House D24 resonance fructifying next semester',
      notes: 'Pursuing PhD in Computational Biology.',
      createdAt: '2026-08-05T09:00:00Z',
      updatedAt: '2026-08-25T11:30:00Z'
    }
  ],

  cases: [
    {
      id: 'case_2026_0101',
      caseNumber: 'CASE-2026-0101',
      clientId: 'cli_aarav_01',
      clientName: 'Aarav Sharma',
      clientRefId: 'VJ-CLI-8491',
      themeId: 'career_job',
      themeTitleEn: 'Career & Professional Growth',
      themeTitleHi: 'कार्यक्षेत्र एवं पदोन्नति',
      targetQuestion: 'When is the optimal window to accept the overseas leadership offer?',
      stage: 'REVIEW',
      priority: 'HIGH',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      practitionerRole: 'Principal Astrologer',
      createdDate: '2026-08-10',
      lastActivityDate: '2026-08-26',
      consultationDate: '2026-08-28',
      reportStatus: 'READY_FOR_REVIEW',
      timingAlertIndicator: {
        hasAlert: true,
        title: 'Double Transit on 10th Bhava',
        confluence: 'HIGH_CONFLUENCE',
        windowDate: '2026-09-15 to 2026-11-20'
      },
      nextAction: 'Finalize Executive Dossier & review D10 Dashamsha resonance',
      nextActionDueDate: '2026-08-29',
      deterministicFindings: {
        natalPromiseVerdict: 'High executive authority promise with 10th Lord in 9th bhava exalted.',
        keySupportingFactors: [
          'Sun-Mercury Budhaditya in 10th House (D1)',
          'D10 Lagna Lord Mars well-placed in Kendra with 34 SAV bindus',
          'Jupiter Mahadasha operating with Saturn Antardasha'
        ],
        primaryDasha: 'Jupiter-Saturn-Mercury',
        primaryTransitTrigger: 'Jupiter transits 10th sign aspecting natal Sun',
        projectedFructificationAge: 'Age 34 (Sep - Nov 2026)',
        vargaAffirmation: 'D10 confirms institutional authority in multinational setup'
      },
      timingWindowsSummary: 'Primary Window: Sep 2026 – Nov 2026 (★★★★ High Confluence). Secondary Consolidation: Feb 2027.',
      practitionerPrivateNotes: 'Client is hesitant due to relocation of family. Advise gradual transition. Recommend Shiva Panchakshara Stotra for clarity under Saturn sub-period.',
      clientFacingSummaryNotes: 'The upcoming transit of Jupiter through your 10th house creates an exceptionally strong window for career elevation and overseas executive authority.',
      clientQuestionsList: [
        {
          id: 'q1',
          question: 'Will the Singapore relocation be beneficial for long-term wealth?',
          answer: 'Yes, 9th/11th lord connection indicates wealth accumulation through foreign territories.'
        },
        {
          id: 'q2',
          question: 'Should I negotiate equity or fixed compensation?',
          answer: '11th house strength favors performance equity vesting over 3 years.'
        }
      ],
      prescribedRemedies: [
        {
          id: 'rem1',
          title: 'Brihaspati Stotram chanting on Thursdays',
          category: 'MANTRA',
          description: 'Recite Brihaspati Beej Mantra 108 times at sunrise facing East.',
          duration: '40 days',
          isActive: true
        },
        {
          id: 'rem2',
          title: 'Yellow grain / Chana dal dana',
          category: 'DANA',
          description: 'Donate yellow pulses or religious books to teachers or students.',
          duration: 'Thursdays during transit',
          isActive: true
        }
      ],
      followUpPlan: {
        recommendedDate: '2026-09-10',
        reason: 'Review Singapore contract draft terms before transit activation',
        specificTimingEvent: 'Jupiter ingress trigger',
        completed: false
      },
      associatedReportIds: ['rep_2026_0101']
    },
    {
      id: 'case_2026_0102',
      caseNumber: 'CASE-2026-0102',
      clientId: 'cli_priyanka_02',
      clientName: 'Priyanka Patel',
      clientRefId: 'VJ-CLI-8492',
      themeId: 'marriage_relationship',
      themeTitleEn: 'Marriage & Relationship Timing',
      themeTitleHi: 'विवाह एवं संबंध काल',
      targetQuestion: 'Is the alliance from Mumbai auspicious and when will the ceremony fructify?',
      stage: 'CONSULTATION',
      priority: 'HIGH',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      practitionerRole: 'Principal Astrologer',
      createdDate: '2026-08-15',
      lastActivityDate: '2026-08-27',
      consultationDate: '2026-08-29',
      reportStatus: 'DRAFT',
      timingAlertIndicator: {
        hasAlert: true,
        title: '7th Bhava Transit Confluence',
        confluence: 'HIGH_CONFLUENCE',
        windowDate: '2026-10-10 to 2027-02-15'
      },
      nextAction: 'Conduct family consultation session on Google Meet / In-person',
      nextActionDueDate: '2026-08-29',
      deterministicFindings: {
        natalPromiseVerdict: 'Strong marital harmony promise with Venus in Trikona in D9.',
        keySupportingFactors: [
          '7th Lord Sun in 10th with benefic aspect',
          'D9 Navamsha Lagna aspected by Jupiter',
          'Venus Mahadasha sub-period active'
        ],
        primaryDasha: 'Venus-Mercury-Venus',
        primaryTransitTrigger: 'Jupiter aspecting natal 7th house and lagna',
        projectedFructificationAge: 'Age 31 (Late 2026 - Early 2027)',
        vargaAffirmation: 'D9 confirms stable, dharmic companionship with shared values'
      },
      timingWindowsSummary: 'Peak Fructification Window: Oct 2026 to Feb 2027 (★★★★ High Confluence).',
      practitionerPrivateNotes: 'Ashtakoot score is 29.5/36 with no Nadi dosha. Match is harmonious. Recommend Vivaha Muhurta selection in Margashirsha month.',
      clientFacingSummaryNotes: 'The horoscopic harmony is well-balanced. Both the D1 and D9 charts confirm mutual respect, intellectual companionship, and auspicious timing for union in late 2026.',
      clientQuestionsList: [
        {
          id: 'q_pri_1',
          question: 'Are there any hidden Manglik or dosha factors in the chart?',
          answer: 'No malefic affliction in 7th or 8th bhavas. Mangal is yogakaraka in Upachaya house.'
        }
      ],
      prescribedRemedies: [
        {
          id: 'rem_pri_1',
          title: 'Gauri Shankar worship & Shukra Aradhana',
          category: 'MANTRA',
          description: 'Offer white flowers and light ghee lamp on Friday evenings.',
          duration: 'Ongoing until wedding',
          isActive: true
        }
      ],
      associatedReportIds: []
    },
    {
      id: 'case_2026_0103',
      caseNumber: 'CASE-2026-0103',
      clientId: 'cli_vikram_03',
      clientName: 'Vikramaditya Roy',
      clientRefId: 'VJ-CLI-8493',
      themeId: 'house_property',
      themeTitleEn: 'Property, Land & Real Estate',
      themeTitleHi: 'भूमि, भवन एवं संपत्ति योग',
      targetQuestion: 'Auspicious timing for acquiring the 5-acre industrial development parcel?',
      stage: 'REPORT_PREP',
      priority: 'MEDIUM',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      practitionerRole: 'Principal Astrologer',
      createdDate: '2026-08-08',
      lastActivityDate: '2026-08-25',
      consultationDate: '2026-08-12',
      reportStatus: 'DRAFT',
      nextAction: 'Compile Full Classical Dossier with D4 Chaturthamsha verification',
      nextActionDueDate: '2026-08-30',
      deterministicFindings: {
        natalPromiseVerdict: 'Exceptional land ownership yoga (Bhoomi Labha Yoga) via 4th Lord Saturn in exalted dignity.',
        keySupportingFactors: [
          '4th house has 36 SAV bindus',
          'Mars-Saturn mutual sambandha with benefic Jupiter reception',
          'D4 chart shows strong 4th/11th exchange'
        ],
        primaryDasha: 'Saturn-Jupiter-Mars',
        primaryTransitTrigger: 'Saturn transiting 5th house aspecting 4th',
        projectedFructificationAge: 'Age 42',
        vargaAffirmation: 'D4 Chaturthamsha confirms commercial property acquisition'
      },
      timingWindowsSummary: 'Optimal Registry Window: Nov 2026 – Jan 2027.',
      practitionerPrivateNotes: 'Advise client to complete soil inspection and legal registry during Shukla Paksha in auspicious Chitra/Rohini nakshatra.',
      clientFacingSummaryNotes: 'The astrological promise for extensive land holding is remarkably strong. The period between November 2026 and January 2027 presents an ideal acquisition window.',
      clientQuestionsList: [],
      prescribedRemedies: [
        {
          id: 'rem_vik_1',
          title: 'Bhumi Pujan & Hanuman Chalisa',
          category: 'BEHAVIORAL',
          description: 'Conduct ceremonial soil honoring prior to contract signing.',
          duration: 'At acquisition date',
          isActive: true
        }
      ],
      associatedReportIds: []
    },
    {
      id: 'case_2026_0104',
      caseNumber: 'CASE-2026-0104',
      clientId: 'cli_ananya_04',
      clientName: 'Ananya Deshmukh',
      clientRefId: 'VJ-CLI-8494',
      themeId: 'education',
      themeTitleEn: 'Higher Education & Scholarship',
      themeTitleHi: 'उच्च शिक्षा एवं शोध योग',
      targetQuestion: 'Will European doctoral scholarship be granted for Spring 2027 intake?',
      stage: 'DELIVERED',
      priority: 'MEDIUM',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      practitionerRole: 'Principal Astrologer',
      createdDate: '2026-08-05',
      lastActivityDate: '2026-08-20',
      consultationDate: '2026-08-18',
      reportStatus: 'DELIVERED',
      nextAction: 'Scheduled follow-up upon university notification in December',
      nextActionDueDate: '2026-12-05',
      deterministicFindings: {
        natalPromiseVerdict: 'Distinguished scholarly intellect with 5th Lord Jupiter in 9th bhava.',
        keySupportingFactors: [
          'D24 Siddhamsa shows Jupiter in Kendra',
          '9th house aspected by Sun and Mercury',
          'Mars-Rahu dasha activating foreign scholarly travel'
        ],
        primaryDasha: 'Mars-Rahu-Jupiter',
        primaryTransitTrigger: 'Jupiter aspecting 9th house of higher learning',
        projectedFructificationAge: 'Age 25 (Spring 2027)',
        vargaAffirmation: 'D24 validates doctoral level research and institutional fellowship'
      },
      timingWindowsSummary: 'Scholarship Grant Window: Dec 2026 – Feb 2027.',
      practitionerPrivateNotes: 'Native is extremely diligent. Delivered Full Classical Dossier with research remedies.',
      clientFacingSummaryNotes: 'Your natal promise strongly supports international doctoral scholarship and pioneering academic contributions.',
      clientQuestionsList: [],
      prescribedRemedies: [
        {
          id: 'rem_an_1',
          title: 'Saraswati Vandana & Gayatri Japa',
          category: 'MANTRA',
          description: 'Daily morning recitation of Saraswati Beej Mantra.',
          duration: 'Daily during application cycle',
          isActive: true
        }
      ],
      associatedReportIds: ['rep_2026_0104']
    }
  ],

  consultations: [
    {
      id: 'con_2026_0501',
      consultationNumber: 'CNS-2026-0501',
      caseId: 'case_2026_0101',
      clientId: 'cli_aarav_01',
      clientName: 'Aarav Sharma',
      clientRefId: 'VJ-CLI-8491',
      themeId: 'career_job',
      question: 'Leadership role transition & overseas venture timing',
      scheduledDateTime: '2026-08-28T16:00:00Z',
      durationMinutes: 45,
      status: 'SCHEDULED',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      meetingFormat: 'VIDEO',
      sessionNotes: 'Prepared D10 analysis and 10th bhava SAV breakdown (34 bindus).',
      clientSummary: 'Discussing Singapore contract terms and optimal transition window in Sep-Nov 2026.',
      prescribedRemediesSummary: 'Brihaspati Stotram & Thursday yellow pulse donation.',
      reportId: 'rep_2026_0101',
      reportStatus: 'READY_FOR_REVIEW',
      createdAt: '2026-08-20T10:00:00Z',
      updatedAt: '2026-08-27T08:00:00Z'
    },
    {
      id: 'con_2026_0502',
      consultationNumber: 'CNS-2026-0502',
      caseId: 'case_2026_0102',
      clientId: 'cli_priyanka_02',
      clientName: 'Priyanka Patel',
      clientRefId: 'VJ-CLI-8492',
      themeId: 'marriage_relationship',
      question: 'Alliance verification and auspicious Vivaha timeline',
      scheduledDateTime: '2026-08-29T11:00:00Z',
      durationMinutes: 60,
      status: 'SCHEDULED',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      meetingFormat: 'IN_PERSON',
      sessionNotes: 'Kundali Milan chart prepared with D9 Navamsha and 7th bhava analysis.',
      clientSummary: 'Family meeting to discuss compatibility factors and auspicious wedding dates.',
      prescribedRemediesSummary: 'Gauri Shankar Aradhana.',
      reportStatus: 'DRAFT',
      createdAt: '2026-08-22T14:00:00Z',
      updatedAt: '2026-08-27T09:30:00Z'
    },
    {
      id: 'con_2026_0503',
      consultationNumber: 'CNS-2026-0503',
      caseId: 'case_2026_0104',
      clientId: 'cli_ananya_04',
      clientName: 'Ananya Deshmukh',
      clientRefId: 'VJ-CLI-8494',
      themeId: 'education',
      question: 'PhD Admission abroad & fellowship timing',
      scheduledDateTime: '2026-08-18T15:30:00Z',
      durationMinutes: 45,
      status: 'COMPLETED',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      meetingFormat: 'VIDEO',
      sessionNotes: 'Completed thorough 9th house and D24 evaluation. Client was very satisfied.',
      clientSummary: 'Discussed scholarship timing windows (Dec 2026 - Feb 2027) and research focus.',
      prescribedRemediesSummary: 'Saraswati Mantra and Gayatri Japa.',
      reportId: 'rep_2026_0104',
      reportStatus: 'DELIVERED',
      createdAt: '2026-08-05T09:00:00Z',
      updatedAt: '2026-08-18T16:30:00Z'
    }
  ],

  reports: [
    {
      id: 'rep_2026_0101',
      reportNumber: 'REP-2026-0801',
      caseId: 'case_2026_0101',
      clientId: 'cli_aarav_01',
      clientName: 'Aarav Sharma',
      clientRefId: 'VJ-CLI-8491',
      reportType: 'EXECUTIVE_BRIEF',
      title: 'Executive Career Transition & Overseas Window Assessment',
      subtitle: 'Deterministic Vedic Analysis of 10th House, D10 Dashamsha & Dasha Confluence',
      status: 'READY_FOR_REVIEW',
      version: '1.1',
      generatedDate: '2026-08-26',
      generatedBy: 'Acharya Vidyadhar Shastri',
      practitionerCredentials: 'Jyotish Visharad, Parashari Scholar',
      themeId: 'career_job',
      targetQuestion: 'When is the optimal window to accept the overseas leadership offer?',
      config: {
        includeCover: true,
        includeBirthCoordinates: true,
        includeLifeStageContext: true,
        includeNatalPromise: true,
        includeD1Chart: true,
        includeThematicVarga: true,
        includeD9Navamsha: true,
        includeBhavaEvidence: true,
        includePlanetaryStrengths: true,
        includeAshtakvarga: true,
        includeDashaGocharMatrix: true,
        includePhalaBhutaWindows: true,
        includeClassicalReasoning: true,
        includeRemedies: true,
        includePractitionerNotes: true,
        includeConsultationSummary: true,
        includeRawForensicTables: false
      },
      executiveSummaryText:
        'The natal chart reveals a powerful Raj Yoga anchored in the 10th house of professional authority. The ongoing Jupiter-Saturn dasha coupled with the upcoming Jupiter transit through the 10th sign creates an exceptional career elevation window between September and November 2026.',
      timingVerdictText:
        'Primary Fructification Window: September 15, 2026 – November 20, 2026 (★★★★ HIGH CONFLUENCE). Secondary Consolidation: February 2027.',
      remediesText:
        '1. Brihaspati Beej Mantra chanting on Thursday mornings (108 times).\n2. Dana of yellow split-peas (chana dal) to educators or learners.\n3. Water offerings to rising Sun with Surya Gayatri.',
      pdfExportCount: 2
    },
    {
      id: 'rep_2026_0104',
      reportNumber: 'REP-2026-0804',
      caseId: 'case_2026_0104',
      clientId: 'cli_ananya_04',
      clientName: 'Ananya Deshmukh',
      clientRefId: 'VJ-CLI-8494',
      reportType: 'CLASSICAL_DOSSIER',
      title: 'Higher Education & Academic Fellowship Comprehensive Dossier',
      subtitle: 'Evaluation of 5th/9th Bhavas, D24 Siddhamsa & Educational Karmic Arc',
      status: 'DELIVERED',
      version: '1.0',
      generatedDate: '2026-08-19',
      generatedBy: 'Acharya Vidyadhar Shastri',
      practitionerCredentials: 'Jyotish Visharad, Parashari Scholar',
      themeId: 'education',
      targetQuestion: 'Will European doctoral scholarship be granted for Spring 2027 intake?',
      config: {
        includeCover: true,
        includeBirthCoordinates: true,
        includeLifeStageContext: true,
        includeNatalPromise: true,
        includeD1Chart: true,
        includeThematicVarga: true,
        includeD9Navamsha: true,
        includeBhavaEvidence: true,
        includePlanetaryStrengths: true,
        includeAshtakvarga: true,
        includeDashaGocharMatrix: true,
        includePhalaBhutaWindows: true,
        includeClassicalReasoning: true,
        includeRemedies: true,
        includePractitionerNotes: true,
        includeConsultationSummary: true,
        includeRawForensicTables: true
      },
      executiveSummaryText:
        'The natal geometry exhibits Saraswati Yoga and an exalted 5th lord, validating superior aptitude in computational scientific inquiry and doctoral study in European academies.',
      timingVerdictText:
        'Fellowship Notification Window: December 2026 – February 2027 (★★★★ HIGH CONFLUENCE).',
      remediesText: 'Daily Saraswati Japa & lighting ghee lamp during Brahma Muhurta.',
      pdfExportCount: 4,
      approvedDate: '2026-08-19',
      deliveredDate: '2026-08-20'
    }
  ],

  timingEvents: [
    {
      id: 'time_ev_01',
      clientId: 'cli_aarav_01',
      clientName: 'Aarav Sharma',
      clientRefId: 'VJ-CLI-8491',
      themeId: 'career_job',
      eventTitle: 'Double-Transit Activation on 10th House (Pisces)',
      eventType: 'DOUBLE_TRANSIT_ACTIVATION',
      triggerDate: '2026-09-18',
      projectedAge: '34 Years',
      relevantDasha: 'Jupiter-Saturn-Mercury',
      transitTrigger: 'Jupiter and Saturn simultaneous aspect on 10th bhava',
      confluenceLevel: 'HIGH_CONFLUENCE',
      confluenceLabel: '★★★★ HIGH CONFLUENCE',
      daysRemaining: 21,
      daysUntilEvent: 21,
      title: 'Double-Transit Activation on 10th House (Pisces)',
      summary: 'Simultaneous Jupiter and Saturn transit triggers natal 10th house karmic fruition for executive promotion.',
      recommendedAction: 'Schedule pre-transition consultation and finalize overseas employment terms.',
      caseId: 'case_2026_0101'
    },
    {
      id: 'time_ev_02',
      clientId: 'cli_priyanka_02',
      clientName: 'Priyanka Patel',
      clientRefId: 'VJ-CLI-8492',
      themeId: 'marriage_relationship',
      eventTitle: 'Jupiter Ingress Activating 7th Lord & Navamsha Lagna',
      eventType: 'JUPITER_TRANSIT',
      triggerDate: '2026-10-12',
      projectedAge: '30 Years 11 Months',
      relevantDasha: 'Venus-Mercury',
      transitTrigger: 'Jupiter direct aspect on 7th bhava lord',
      confluenceLevel: 'HIGH_CONFLUENCE',
      confluenceLabel: '★★★★ HIGH CONFLUENCE',
      daysRemaining: 45,
      daysUntilEvent: 45,
      title: 'Jupiter Ingress Activating 7th Lord & Navamsha Lagna',
      summary: 'Benefic Jupiter aspect energizes marital karmic fructification and formal alliance engagement.',
      recommendedAction: 'Finalize Vivaha Muhurta selection for winter 2026.',
      caseId: 'case_2026_0102'
    },
    {
      id: 'time_ev_03',
      clientId: 'cli_vikram_03',
      clientName: 'Vikramaditya Roy',
      clientRefId: 'VJ-CLI-8493',
      themeId: 'house_property',
      eventTitle: 'Mars Sub-Period Ingress in 4th Bhava Lord Saturn',
      eventType: 'ANTARDASHA_SHIFT',
      triggerDate: '2026-11-04',
      projectedAge: '42 Years 8 Months',
      relevantDasha: 'Saturn-Jupiter-Mars',
      transitTrigger: 'Bhumi-Karaka Mars activation in D4',
      confluenceLevel: 'FAVORABLE_ALIGNMENT',
      confluenceLabel: '★★★☆ FAVORABLE ALIGNMENT',
      daysRemaining: 68,
      daysUntilEvent: 68,
      title: 'Mars Sub-Period Ingress in 4th Bhava Lord Saturn',
      summary: 'Bhoomi karaka Mars activates 4th house Ashtakvarga bindus for industrial land acquisition.',
      recommendedAction: 'Prepare land registry deeds during Shukla Paksha.',
      caseId: 'case_2026_0103'
    },
    {
      id: 'time_ev_04',
      clientId: 'cli_ananya_04',
      clientName: 'Ananya Deshmukh',
      clientRefId: 'VJ-CLI-8494',
      themeId: 'education',
      eventTitle: '9th House Transiting Jupiter aspecting Natal Moon',
      eventType: 'JUPITER_TRANSIT',
      triggerDate: '2026-12-15',
      projectedAge: '25 Years 3 Months',
      relevantDasha: 'Mars-Rahu-Jupiter',
      transitTrigger: 'Guru Drishti on 9th bhava & D24 Lagnesha',
      confluenceLevel: 'HIGH_CONFLUENCE',
      confluenceLabel: '★★★★ HIGH CONFLUENCE',
      daysRemaining: 109,
      daysUntilEvent: 109,
      title: '9th House Transiting Jupiter aspecting Natal Moon',
      summary: 'Institutional scholarship grant formal acceptance window in European university.',
      recommendedAction: 'Verify visa documentation and academic endorsement.',
      caseId: 'case_2026_0104'
    }
  ],

  followUps: [
    {
      id: 'fol_01',
      caseId: 'case_2026_0102',
      clientId: 'cli_priyanka_02',
      clientName: 'Priyanka Patel',
      clientRefId: 'VJ-CLI-8492',
      themeId: 'marriage_relationship',
      category: 'Relationship / Vivaha',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      reason: 'Review family match discussions and finalize Vivaha dates',
      dueDate: '2026-08-30',
      status: 'DUE_THIS_WEEK',
      priority: 'HIGH',
      relatedTimingEvent: 'Jupiter aspect on 7th Lord in Oct 2026',
      practitionerNote: 'Ensure client has completed Gauri Shankar aradhana.'
    },
    {
      id: 'fol_02',
      caseId: 'case_2026_0101',
      clientId: 'cli_aarav_01',
      clientName: 'Aarav Sharma',
      clientRefId: 'VJ-CLI-8491',
      themeId: 'career_job',
      category: 'Career / Overseas',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      reason: 'Review Singapore contract draft terms before transit activation',
      dueDate: '2026-09-10',
      status: 'UPCOMING',
      priority: 'HIGH',
      relatedTimingEvent: 'Double-Transit activation on 10th House (Pisces)',
      practitionerNote: 'Check relocation compensation clause.'
    },
    {
      id: 'fol_03',
      caseId: 'case_2026_0103',
      clientId: 'cli_vikram_03',
      clientName: 'Vikramaditya Roy',
      clientRefId: 'VJ-CLI-8493',
      themeId: 'house_property',
      category: 'Property / Land',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      reason: 'Verify industrial land registration auspicious muhurta',
      dueDate: '2026-09-25',
      status: 'UPCOMING',
      priority: 'MEDIUM',
      practitionerNote: 'Select Muhurta in Shukla Paksha Chitra nakshatra.'
    },
    {
      id: 'fol_04',
      caseId: 'case_2026_0104',
      clientId: 'cli_ananya_04',
      clientName: 'Ananya Deshmukh',
      clientRefId: 'VJ-CLI-8494',
      themeId: 'education',
      category: 'Education / Fellowship',
      assignedPractitioner: 'Acharya Vidyadhar Shastri',
      reason: 'PhD scholarship acceptance confirmation check-in',
      dueDate: '2026-12-05',
      status: 'UPCOMING',
      priority: 'MEDIUM'
    }
  ],

  timeline: [
    {
      id: 'tl_01',
      clientId: 'cli_aarav_01',
      timestamp: '2026-05-10T10:00:00Z',
      type: 'INTAKE',
      title: 'Client Intake & Natal Chart Verified',
      description: 'Initial intake completed. Mithuna Lagna and Vrishchika Rashi calculated with precision.',
      practitioner: 'Acharya Vidyadhar Shastri'
    },
    {
      id: 'tl_02',
      clientId: 'cli_aarav_01',
      timestamp: '2026-08-10T11:00:00Z',
      type: 'CASE_CREATED',
      title: 'Case Opened: Career Leadership Transition (CASE-2026-0101)',
      description: 'Opened dedicated inquiry into overseas leadership offer and 10th house timing.',
      practitioner: 'Acharya Vidyadhar Shastri',
      relatedCaseId: 'case_2026_0101'
    },
    {
      id: 'tl_03',
      clientId: 'cli_aarav_01',
      timestamp: '2026-08-26T15:30:00Z',
      type: 'REPORT_GENERATED',
      title: 'Executive Brief Generated (REP-2026-0801)',
      description: 'Compiled high-confluence window (Sep 15 - Nov 20, 2026) and D10 analysis.',
      practitioner: 'Acharya Vidyadhar Shastri',
      relatedCaseId: 'case_2026_0101',
      relatedReportId: 'rep_2026_0101'
    }
  ]
};

// -------------------------------------------------------------
// Database Operations
// -------------------------------------------------------------

function loadDatabase(): PracticeDatabase {
  try {
    const raw = localStorage.getItem(DB_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PracticeDatabase;
      if (parsed && Array.isArray(parsed.clients) && parsed.clients.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Failed to load practice database from localStorage:', e);
  }
  // Initialize and persist seed
  saveDatabase(INITIAL_PRACTICE_DATA);
  return INITIAL_PRACTICE_DATA;
}

function saveDatabase(data: PracticeDatabase): void {
  try {
    localStorage.setItem(DB_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save practice database to localStorage:', e);
  }
}

// -------------------------------------------------------------
// Public Service Methods
// -------------------------------------------------------------

export function getPracticeSettings(): PracticeProfileSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const isAssumedName = parsed.primaryPractitionerName === 'Acharya Vidyadhar Shastri';
        const isAssumedPhone = parsed.phone === '+91 98765 43210' || parsed.phone === '9876543210';
        const isAssumedEmail = parsed.email === 'acharya.vidyadhar@vedangjyotish.org';
        const isAssumedPractice = parsed.practiceName === 'Vedang Jyotisham Vedic Consulting';
        const isAssumedSig = parsed.signatureText === 'Acharya Vidyadhar Shastri (Jyotish Visharad)';

        return {
          ...DEFAULT_PRACTICE_SETTINGS,
          ...parsed,
          primaryPractitionerName: isAssumedName ? '' : (parsed.primaryPractitionerName || ''),
          phone: isAssumedPhone ? '' : (parsed.phone || ''),
          email: isAssumedEmail ? '' : (parsed.email || ''),
          practiceName: isAssumedPractice ? '' : (parsed.practiceName || ''),
          signatureText: isAssumedSig ? '' : (parsed.signatureText || '')
        };
      }
    }
  } catch (e) {
    console.error('Failed to load practice settings:', e);
  }
  return DEFAULT_PRACTICE_SETTINGS;
}

export function savePracticeSettings(settings: PracticeProfileSettings): void {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save practice settings:', e);
  }
}

// Clients CRM
export function getPracticeClients(): PracticeClient[] {
  const db = loadDatabase();
  return db.clients || [];
}

export function getPracticeClientById(id: string): PracticeClient | undefined {
  const db = loadDatabase();
  return db.clients.find((c) => c.id === id || c.referenceId === id);
}

export function savePracticeClient(client: PracticeClient): PracticeClient {
  const db = loadDatabase();
  const index = db.clients.findIndex((c) => c.id === client.id);
  const now = new Date().toISOString();

  if (index >= 0) {
    db.clients[index] = { ...client, updatedAt: now };
  } else {
    const newClient: PracticeClient = {
      ...client,
      createdAt: client.createdAt || now,
      updatedAt: now
    };
    db.clients.unshift(newClient);

    // Record timeline intake
    db.timeline.unshift({
      id: `tl_${Date.now()}`,
      clientId: newClient.id,
      timestamp: now,
      type: 'INTAKE',
      title: 'Client Intake Created',
      description: `Client record created for ${newClient.name}. Reference: ${newClient.referenceId}`,
      practitioner: getPracticeSettings().primaryPractitionerName
    });
  }

  saveDatabase(db);
  return client;
}

export function createClientFromKundali(
  kundali: CompleteKundali,
  notes: string = '',
  tags: string[] = ['Saved']
): PracticeClient {
  const birth = kundali.birthDetails;
  const clientId = `cli_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
  const refId = `VJ-CLI-${Math.floor(1000 + Math.random() * 9000)}`;
  const lagnaSign = (kundali.charts?.D1 as any)?.ascendantSign || kundali.charts?.D1?.houses?.[0]?.sign || 'Mithuna';
  const moonPlanet = kundali.planets?.find((p) => p.name === 'Moon');
  const moonSign = moonPlanet ? (moonPlanet as any).sign || (moonPlanet as any).currentSign || 'Vrishabha' : 'Vrishabha';
  const nakshatra = moonPlanet ? moonPlanet.nakshatra : 'Rohini';

  // Compute age
  let ageYears = 30;
  if (birth.date) {
    const birthYear = parseInt(birth.date.split('-')[0], 10);
    if (!isNaN(birthYear)) {
      ageYears = Math.max(0, new Date().getFullYear() - birthYear);
    }
  }

  const client: PracticeClient = {
    id: clientId,
    referenceId: refId,
    name: birth.name || 'Client',
    gender: (birth.gender as any) || 'male',
    dateOfBirth: birth.date || '1995-01-01',
    timeOfBirth: birth.time || '12:00',
    placeOfBirth: birth.placeName || 'Vedic Horizon',
    latitude: birth.latitude || 28.6139,
    longitude: birth.longitude || 77.209,
    timezoneOffsetMinutes: birth.timezoneOffsetMinutes || 330,
    lagnaSign: `${lagnaSign}`,
    moonSign: `${moonSign}`,
    nakshatra: nakshatra,
    activeDasha: 'Active Dasha Phase',
    lifeStage: ageYears < 25 ? 'Brahmacharya (Youth/Study)' : ageYears < 55 ? 'Gruhastha (Householder/Career)' : 'Vanaprastha (Maturity/Wisdom)',
    ageYears,
    tags,
    status: 'ACTIVE',
    openCasesCount: 0,
    notes,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    kundaliData: kundali
  };

  return savePracticeClient(client);
}

// Cases Management
export function getPracticeCases(): PracticeCase[] {
  const db = loadDatabase();
  return db.cases || [];
}

export function getPracticeCaseById(id: string): PracticeCase | undefined {
  const db = loadDatabase();
  return db.cases.find((c) => c.id === id || c.caseNumber === id);
}

export function savePracticeCase(caseItem: PracticeCase): PracticeCase {
  const db = loadDatabase();
  const index = db.cases.findIndex((c) => c.id === caseItem.id);
  const now = new Date().toISOString();

  if (index >= 0) {
    db.cases[index] = { ...caseItem, lastActivityDate: now.split('T')[0] };
  } else {
    const newCase = {
      ...caseItem,
      createdDate: caseItem.createdDate || now.split('T')[0],
      lastActivityDate: now.split('T')[0]
    };
    db.cases.unshift(newCase);

    // Update client open cases count
    const client = db.clients.find((c) => c.id === caseItem.clientId);
    if (client) {
      client.openCasesCount = (client.openCasesCount || 0) + 1;
      client.updatedAt = now;
    }

    // Add to timeline
    db.timeline.unshift({
      id: `tl_${Date.now()}`,
      clientId: caseItem.clientId,
      timestamp: now,
      type: 'CASE_CREATED',
      title: `Case Created: ${caseItem.themeTitleEn} (${caseItem.caseNumber})`,
      description: caseItem.targetQuestion,
      practitioner: caseItem.assignedPractitioner,
      relatedCaseId: newCase.id
    });
  }

  saveDatabase(db);
  return caseItem;
}

export function updateCaseStage(caseId: string, stage: CaseStage): boolean {
  const db = loadDatabase();
  const caseItem = db.cases.find((c) => c.id === caseId);
  if (!caseItem) return false;

  const prevStage = caseItem.stage;
  caseItem.stage = stage;
  caseItem.lastActivityDate = new Date().toISOString().split('T')[0];

  // Timeline note
  db.timeline.unshift({
    id: `tl_${Date.now()}`,
    clientId: caseItem.clientId,
    timestamp: new Date().toISOString(),
    type: 'PRACTITIONER_NOTE',
    title: `Case Stage Changed: ${prevStage} → ${stage}`,
    description: `Case ${caseItem.caseNumber} moved to ${stage} stage.`,
    practitioner: caseItem.assignedPractitioner,
    relatedCaseId: caseItem.id
  });

  saveDatabase(db);
  return true;
}

// Consultations
export function getPracticeConsultations(): PracticeConsultation[] {
  const db = loadDatabase();
  return db.consultations || [];
}

export function getPracticeConsultationById(id: string): PracticeConsultation | undefined {
  const db = loadDatabase();
  return db.consultations.find((c) => c.id === id || c.consultationNumber === id);
}

export function savePracticeConsultation(session: PracticeConsultation): PracticeConsultation {
  const db = loadDatabase();
  const index = db.consultations.findIndex((c) => c.id === session.id);
  const now = new Date().toISOString();

  if (index >= 0) {
    db.consultations[index] = { ...session, updatedAt: now };
  } else {
    db.consultations.unshift({
      ...session,
      createdAt: now,
      updatedAt: now
    });

    // Add to timeline
    db.timeline.unshift({
      id: `tl_${Date.now()}`,
      clientId: session.clientId,
      timestamp: now,
      type: 'CONSULTATION',
      title: `Consultation Scheduled (${session.consultationNumber})`,
      description: `Format: ${session.meetingFormat} • Scheduled: ${session.scheduledDateTime}`,
      practitioner: session.assignedPractitioner,
      relatedCaseId: session.caseId
    });
  }

  saveDatabase(db);
  return session;
}

export function updateConsultationStatus(
  id: string,
  status: ConsultationSessionStatus
): boolean {
  const db = loadDatabase();
  const session = db.consultations.find((c) => c.id === id);
  if (!session) return false;

  session.status = status;
  session.updatedAt = new Date().toISOString();

  // If completed, update client lastConsultationDate
  if (status === 'COMPLETED' || status === 'DELIVERED') {
    const client = db.clients.find((c) => c.id === session.clientId);
    if (client) {
      client.lastConsultationDate = new Date().toISOString().split('T')[0];
    }
  }

  saveDatabase(db);
  return true;
}

// Reports
export function getPracticeReports(): PracticeReport[] {
  const db = loadDatabase();
  return db.reports || [];
}

export function getPracticeReportById(id: string): PracticeReport | undefined {
  const db = loadDatabase();
  return db.reports.find((r) => r.id === id || r.reportNumber === id);
}

export function savePracticeReport(report: PracticeReport): PracticeReport {
  const db = loadDatabase();
  const index = db.reports.findIndex((r) => r.id === report.id);

  if (index >= 0) {
    db.reports[index] = report;
  } else {
    db.reports.unshift(report);

    // Link report to case if exists
    if (report.caseId) {
      const caseItem = db.cases.find((c) => c.id === report.caseId);
      if (caseItem && !caseItem.associatedReportIds.includes(report.id)) {
        caseItem.associatedReportIds.push(report.id);
        caseItem.reportStatus = report.status;
      }
    }

    // Timeline event
    db.timeline.unshift({
      id: `tl_${Date.now()}`,
      clientId: report.clientId,
      timestamp: new Date().toISOString(),
      type: 'REPORT_GENERATED',
      title: `Report Created: ${report.title} (${report.reportNumber})`,
      description: `Type: ${report.reportType} • Status: ${report.status}`,
      practitioner: report.generatedBy,
      relatedCaseId: report.caseId,
      relatedReportId: report.id
    });
  }

  saveDatabase(db);
  return report;
}

export function updateReportStatus(reportId: string, status: ReportStatus): boolean {
  const db = loadDatabase();
  const report = db.reports.find((r) => r.id === reportId);
  if (!report) return false;

  report.status = status;
  const now = new Date().toISOString();
  if (status === 'APPROVED') report.approvedDate = now.split('T')[0];
  if (status === 'DELIVERED') report.deliveredDate = now.split('T')[0];

  // Also update case report status
  if (report.caseId) {
    const caseItem = db.cases.find((c) => c.id === report.caseId);
    if (caseItem) {
      caseItem.reportStatus = status;
    }
  }

  saveDatabase(db);
  return true;
}

// Timing Radar
export function getTimingRadarEvents(horizonDays: 30 | 60 | 90 = 60): TimingRadarEvent[] {
  const db = loadDatabase();
  const events = db.timingEvents || [];
  return events.filter((ev) => ev.daysRemaining <= horizonDays);
}

export function addTimingRadarEvent(event: TimingRadarEvent): void {
  const db = loadDatabase();
  db.timingEvents.unshift(event);
  saveDatabase(db);
}

// Follow-ups
export function getPracticeFollowUps(): FollowUpItem[] {
  const db = loadDatabase();
  return db.followUps || [];
}

export function savePracticeFollowUp(item: FollowUpItem): FollowUpItem {
  const db = loadDatabase();
  const index = db.followUps.findIndex((f) => f.id === item.id);
  if (index >= 0) {
    db.followUps[index] = item;
  } else {
    db.followUps.unshift(item);
  }
  saveDatabase(db);
  return item;
}

export function completeFollowUp(id: string, notes: string = ''): boolean {
  const db = loadDatabase();
  const item = db.followUps.find((f) => f.id === id);
  if (!item) return false;

  item.status = 'COMPLETED';
  item.completedAt = new Date().toISOString();
  item.completionNotes = notes;

  // Timeline
  db.timeline.unshift({
    id: `tl_${Date.now()}`,
    clientId: item.clientId,
    timestamp: new Date().toISOString(),
    type: 'FOLLOW_UP',
    title: `Follow-Up Completed: ${item.reason}`,
    description: notes || 'Follow-up consultation milestone verified.',
    practitioner: getPracticeSettings().primaryPractitionerName,
    relatedCaseId: item.caseId
  });

  saveDatabase(db);
  return true;
}

// Helper exports for UI builders
export function getFollowUpItems(): FollowUpItem[] {
  const db = loadDatabase();
  return db.followUps || [];
}

export function completeFollowUpItem(id: string, notes?: string): boolean {
  return completeFollowUp(id, notes);
}

export function createPracticeClient(
  clientData: Omit<PracticeClient, 'id' | 'referenceId' | 'createdAt' | 'updatedAt'>
): PracticeClient {
  const newClient: PracticeClient = {
    ...clientData,
    id: `cli_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    referenceId: `VJ-CLI-${Math.floor(1000 + Math.random() * 9000)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  return savePracticeClient(newClient);
}

export function createPracticeCase(
  caseData: Omit<PracticeCase, 'id' | 'caseNumber' | 'createdDate' | 'lastActivityDate'>
): PracticeCase {
  const newCase: PracticeCase = {
    ...caseData,
    id: `case_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    caseNumber: `VJ-CAS-${Math.floor(1000 + Math.random() * 9000)}`,
    createdDate: new Date().toISOString().split('T')[0],
    lastActivityDate: new Date().toISOString().split('T')[0]
  };
  return savePracticeCase(newCase);
}

export function getClientTimeline(clientId: string): ClientTimelineEvent[] {
  const db = loadDatabase();
  return (db.timeline || []).filter((t) => t.clientId === clientId);
}

export function addClientTimelineEvent(event: ClientTimelineEvent): void {
  const db = loadDatabase();
  db.timeline.unshift(event);
  saveDatabase(db);
}

// Practice Analytics Engine
export function getPracticeAnalytics(
  period: '7d' | '30d' | '90d' | 'all' = '30d'
): PracticeAnalyticsSummary {
  const db = loadDatabase();
  const clients = db.clients || [];
  const cases = db.cases || [];
  const consultations = db.consultations || [];
  const reports = db.reports || [];
  const followUps = db.followUps || [];

  const pipelineDistribution: Record<CaseStage, number> = {
    NEW: cases.filter((c) => c.stage === 'NEW').length,
    ANALYSIS: cases.filter((c) => c.stage === 'ANALYSIS').length,
    REVIEW: cases.filter((c) => c.stage === 'REVIEW').length,
    CONSULTATION: cases.filter((c) => c.stage === 'CONSULTATION').length,
    REPORT_PREP: cases.filter((c) => c.stage === 'REPORT_PREP').length,
    DELIVERED: cases.filter((c) => c.stage === 'DELIVERED').length,
    FOLLOW_UP: cases.filter((c) => c.stage === 'FOLLOW_UP').length
  };

  // Theme requests breakdown
  const themeCounts: Record<string, { name: string; count: number }> = {};
  cases.forEach((c) => {
    if (!themeCounts[c.themeId]) {
      themeCounts[c.themeId] = { name: c.themeTitleEn || c.themeId, count: 0 };
    }
    themeCounts[c.themeId].count += 1;
  });

  const totalCases = cases.length || 1;
  const themeRequestsDistribution = Object.entries(themeCounts).map(([themeId, data]) => ({
    themeId: themeId as any,
    themeName: data.name,
    count: data.count,
    percentage: Math.round((data.count / totalCases) * 100)
  }));

  const completedFollowUps = followUps.filter((f) => f.status === 'COMPLETED').length;
  const totalFollowUps = followUps.length || 1;

  return {
    period,
    totalClients: clients.length,
    totalActiveClients: clients.filter((c) => c.status === 'ACTIVE').length,
    newClientsInPeriod: Math.min(clients.length, 3),
    totalActiveCases: cases.filter((c) => c.stage !== 'DELIVERED').length,
    casesOpenedInPeriod: cases.length,
    casesCompletedInPeriod: cases.filter((c) => c.stage === 'DELIVERED').length,
    consultationsConducted: consultations.filter((c) => c.status === 'COMPLETED').length,
    consultationHoursTotal: consultations.reduce((acc, c) => acc + (c.durationMinutes || 45) / 60, 0),
    reportsGenerated: reports.length,
    reportsAwaitingReview: reports.filter((r) => r.status === 'READY_FOR_REVIEW' || r.status === 'DRAFT').length,
    reportsDelivered: reports.filter((r) => r.status === 'DELIVERED').length,
    averageReportTurnaroundDays: 2.4,
    followUpsDueCount: followUps.filter((f) => f.status !== 'COMPLETED').length,
    followUpsCompletedRate: Math.round((completedFollowUps / totalFollowUps) * 100),
    pipelineDistribution,
    mostRequestedThemes: themeRequestsDistribution.map(t => ({ themeKey: t.themeId, count: t.count })),
    themeRequestsDistribution,
    monthlyCaseTrends: [
      { month: 'Jun 2026', opened: 8, completed: 7, consultations: 12 },
      { month: 'Jul 2026', opened: 11, completed: 9, consultations: 15 },
      { month: 'Aug 2026', opened: 14, completed: 12, consultations: 18 }
    ]
  };
}
